import type { BskyPost } from '@/lib/bluesky-fetcher';
import { getBaseUrl } from '@/lib/env';

interface Post {
  id: string;
  title: string;
  thumbnail: string;
  channel: {
    name: string;
    avatar: string;
  };
  views: number;
  timestamp: Date;
  duration: string;
  text: string;
  createdAt: string;
}

interface ApiPost {
  id: string;
  title: string;
  thumbnail: string;
  channel: {
    name: string;
    avatar: string;
  };
  views: number;
  timestamp: string;
  duration: string;
  text: string;
  createdAt: string;
}

// Function to clear all client-side caches
export const clearClientCache = async () => {
  try {
    const cacheKeys = await window.caches.keys();
    await Promise.all(
      cacheKeys.map(key => window.caches.delete(key))
    );
    console.log('All client caches cleared');
  } catch (error) {
    console.error('Error clearing cache:', error);
    throw error;
  }
};

export const fetchBskyFeed = async (): Promise<Post[]> => {
  try {
    console.log('🔄 [BLUESKY] Starting feed fetch...');
    const baseUrl = getBaseUrl();
    const apiUrl = baseUrl ? `${baseUrl}/api/bluesky-feed` : '/api/bluesky-feed';
    console.log('🌐 [BLUESKY] Using API URL:', apiUrl);
    
    const response = await window.fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const rawData = await response.json();
    console.log('📦 [BLUESKY] Raw API response:', rawData);
    
    // Type guard to check if the response is an array
    if (!Array.isArray(rawData)) {
      throw new Error('API response is not an array');
    }

    // Convert timestamp strings back to Date objects
    const processedPosts = rawData.map((post: ApiPost, index) => {
      console.log(`🔍 [BLUESKY] Processing post ${index}:`, post);
      
      if (!post || typeof post !== 'object') {
        console.warn(`⚠️ [BLUESKY] Invalid post at index ${index}:`, post);
        return null;
      }

      return {
        ...post,
        timestamp: new Date(post.timestamp)
      };
    }).filter((post): post is Post => post !== null);

    console.log('✅ [BLUESKY] Successfully processed posts:', processedPosts);
    return processedPosts;
  } catch (error) {
    console.error('❌ [BLUESKY] Error fetching Bluesky feed:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch Bluesky feed');
  }
};

// Helper function to process feed data
const processFeedData = (feed: ApiPost[]): Post[] => {
  return feed.map((post, index) => {
    const twsrtUrlMatch = post.text.match(/(?:https:\/\/)?twsrt\.cc\/([a-zA-Z0-9_-]+)/);
    const videoId = twsrtUrlMatch ? twsrtUrlMatch[1] : null;
    
    if (!videoId) {
      console.warn('No video ID found in post:', post.text);
    }
    
    return {
      id: `post-${index}-${post.createdAt}`,
      title: post.text.split('\n')[0],
      thumbnail: videoId ? `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg` : '/placeholder.svg',
      channel: {
        name: 'twsrt.cc',
        avatar: 'https://images.unsplash.com/photo-1649972904349-6e44c42644a7'
      },
      views: 0,
      timestamp: new Date(post.createdAt),
      duration: "0:00", // TODO: Fetch actual duration from YouTube API
      text: post.text,
      createdAt: post.createdAt
    };
  });
};
